import {
  Box,
  FormHelperText,
  ListItemText,
  Select as MUISelect,
  Stack,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Fragment, SyntheticEvent, useRef, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

import { Icon } from '@/components/Icon/Icon';

import { useResponsive } from '@/hooks/useResponsive';

import { elevationLevel } from '@/styles/constants';

import { getDataTestId } from '@/utils/browser';

import {
  GroupedSelectFieldProps,
  GroupedSelectOption,
  GroupedSelectValue,
} from './GroupedSelect.interface';
import {
  flattenOptions,
  getGroupKey,
  getSelectedDisplayLabel,
  GROUPED_SELECT_OTHER_KEY,
  isGroupValue,
  isPerGroupSelection,
} from './GroupedSelect.utils';

import { FieldArrow, FieldArrowButton } from '../Form.styled';
import {
  BottomSheetHandle,
  BottomSheetHeader,
  GroupDivider,
  GroupedSelectBottomSheet,
  GroupedSelectControl,
  GroupedSelectFieldLabel,
  GroupedSelectFormControl,
  GroupedSelectOptionCheckIcon,
  GroupedSelectOptionItem,
  GroupedSelectSelectedValue,
  GroupSection,
  GroupSubheading,
  OptionsContainer,
  OtherOptionSection,
} from './GroupedSelect.styled';

type GroupedSelectSelectIconProps = Readonly<{
  isOpen: boolean;
}>;

function GroupedSelectSelectIcon({ isOpen }: GroupedSelectSelectIconProps) {
  return (
    <FieldArrowButton>
      <FieldArrow name={isOpen ? 'arrow-up' : 'arrow-down'} />
    </FieldArrowButton>
  );
}

type GroupedSelectDisplayValueProps = Readonly<{
  placeholder?: string;
  selectedLabel?: string;
}>;

function GroupedSelectDisplayValue({
  placeholder,
  selectedLabel,
}: GroupedSelectDisplayValueProps) {
  return (
    <GroupedSelectSelectedValue
      {...getDataTestId('grouped-select-selected')}
      component="span"
      variant="body2"
      color={selectedLabel ? undefined : theme => theme.palette.text.secondary}
    >
      {selectedLabel || placeholder}
    </GroupedSelectSelectedValue>
  );
}

function getNativeSelectValue(
  perGroupSelection: boolean,
  value?: GroupedSelectValue,
): string {
  if (perGroupSelection) {
    return '';
  }

  return typeof value === 'string' ? value : '';
}

function getDesktopMenuProps(anchorWidth?: number) {
  return {
    autoFocus: false,
    transitionDuration: 0,
    PaperProps: {
      ...getDataTestId('select-menu'),
      elevation: elevationLevel.m,
      style: anchorWidth
        ? {
            width: anchorWidth,
            maxWidth: anchorWidth,
            minWidth: anchorWidth,
          }
        : undefined,
      sx: (theme: Theme) => ({
        border: `1px solid ${theme.palette.neutral[200]}`,
        padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
        maxHeight: 280,
        '.MuiList-root': {
          minWidth: 0,
          padding: 0,
          width: '100%',
        },
        '.MuiMenuItem-root': {
          whiteSpace: 'normal',
        },
        '.MuiListItemText-root': {
          whiteSpace: 'normal',
        },
      }),
    },
  };
}

type GroupedSelectHelperTextProps = Readonly<{
  error: boolean;
  helperText: string;
}>;

function GroupedSelectHelperText({
  error,
  helperText,
}: GroupedSelectHelperTextProps) {
  if (error) {
    return (
      <FormHelperText error>
        <Stack gap={0.5} component="span">
          <Icon
            name="alert"
            size={20}
            color={(theme: Theme) => theme.palette.error.main}
          />
          {helperText}
        </Stack>
      </FormHelperText>
    );
  }

  return <FormHelperText>{helperText}</FormHelperText>;
}

type ApplyGroupedSelectSelectionParams = Readonly<{
  perGroupSelection: boolean;
  drawerSelection: GroupedSelectValue;
  option: GroupedSelectOption;
  groupKey: string;
  onChange?: GroupedSelectFieldProps['onChange'];
  onSelectChange?: GroupedSelectFieldProps['onSelectChange'];
  setDrawerSelection: (value: GroupedSelectValue) => void;
  onClose: () => void;
}>;

function applyGroupedSelectSelection({
  perGroupSelection,
  drawerSelection,
  option,
  groupKey,
  onChange,
  onSelectChange,
  setDrawerSelection,
  onClose,
}: ApplyGroupedSelectSelectionParams) {
  if (perGroupSelection) {
    const currentValue = isGroupValue(drawerSelection) ? drawerSelection : {};
    const nextValue =
      groupKey === GROUPED_SELECT_OTHER_KEY
        ? { [GROUPED_SELECT_OTHER_KEY]: option.value }
        : {
            ...currentValue,
            [groupKey]: option.value,
          };

    if (groupKey !== GROUPED_SELECT_OTHER_KEY) {
      delete nextValue[GROUPED_SELECT_OTHER_KEY];
    }

    setDrawerSelection(nextValue);
    onChange?.(nextValue, { option, groupKey });
    onSelectChange?.(nextValue);
    return;
  }

  setDrawerSelection(option.value);
  onChange?.(option.value, { option, groupKey });
  onSelectChange?.(option.value);
  onClose();
}

export function GroupedSelectField({
  label,
  title,
  placeholder,
  groups,
  otherOption,
  selectionMode,
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  sx,
  drawerOptionContainerSx,
  value,
  onChange,
  onBlur,
  onSelectChange,
  onOpenChange,
  name = 'grouped-select',
}: Readonly<GroupedSelectFieldProps>) {
  const { isDesktop } = useResponsive();
  const selectAnchorRef = useRef<HTMLDivElement>(null);
  const { value: isOpen, setFalse: hide, setTrue: show } = useBoolean();
  const perGroupSelection = isPerGroupSelection(groups, selectionMode);
  const emptySelection: GroupedSelectValue = perGroupSelection ? {} : '';
  const [drawerSelection, setDrawerSelection] =
    useState<GroupedSelectValue>(emptySelection);

  const allOptions = flattenOptions(groups, otherOption);
  const selectedLabel = getSelectedDisplayLabel(value, groups, otherOption);

  const openOverlay = () => {
    setDrawerSelection(value ?? emptySelection);
    show();
    onOpenChange?.(true);
  };

  const handleClose = () => {
    hide();
    onBlur?.();
    onOpenChange?.(false);
  };

  const handleDesktopClose = (
    _event: SyntheticEvent,
    reason?: 'backdropClick' | 'escapeKeyDown' | 'selectOption',
  ) => {
    if (perGroupSelection && reason === 'selectOption') {
      return;
    }

    handleClose();
  };

  const handleSelect = (option: GroupedSelectOption, groupKey: string) => {
    applyGroupedSelectSelection({
      perGroupSelection,
      drawerSelection,
      option,
      groupKey,
      onChange,
      onSelectChange,
      setDrawerSelection,
      onClose: handleClose,
    });
  };

  const isOptionSelected = (option: GroupedSelectOption, groupKey: string) => {
    const activeSelection = isOpen ? drawerSelection : value;

    if (perGroupSelection) {
      return (
        isGroupValue(activeSelection) &&
        activeSelection[groupKey] === option.value
      );
    }

    return activeSelection === option.value;
  };

  const renderOption = (option: GroupedSelectOption, groupKey: string) => {
    const isSelected = isOptionSelected(option, groupKey);

    return (
      <GroupedSelectOptionItem
        key={`${groupKey}-${option.value}`}
        value={option.value}
        selected={isSelected}
        onClick={() => handleSelect(option, groupKey)}
        {...getDataTestId('grouped-select-option')}
      >
        <ListItemText
          disableTypography
          primary={
            <Stack
              display={'flex'}
              flexDirection={'column'}
              alignItems={'flex-start'}
              width="100%"
              gap={0.5}
            >
              <Typography component="div" variant="body2">
                {option.label}
              </Typography>
              {option.description && (
                <Typography
                  component="div"
                  variant="caption"
                  color="text.secondary"
                >
                  {option.description}
                </Typography>
              )}
            </Stack>
          }
        />
        {isSelected && (
          <GroupedSelectOptionCheckIcon>
            <Icon
              name="check-mini"
              color={(theme: Theme) => theme.palette.primaries[500]}
            />
          </GroupedSelectOptionCheckIcon>
        )}
      </GroupedSelectOptionItem>
    );
  };

  const renderOptionsContent = () => (
    <OptionsContainer sx={drawerOptionContainerSx}>
      {groups.map((group, groupIndex) => {
        const hasSubheading = !!group.subheading;
        const groupKey = getGroupKey(group, groupIndex);
        const showDivider = groupIndex > 0 && hasSubheading;

        return (
          <GroupSection key={groupKey}>
            {showDivider && <GroupDivider />}
            {hasSubheading && (
              <GroupSubheading variant="body2Bold">
                {group.subheading}
              </GroupSubheading>
            )}
            {group.options.map(option => renderOption(option, groupKey))}
          </GroupSection>
        );
      })}
      {otherOption && (
        <OtherOptionSection>
          {renderOption(otherOption, GROUPED_SELECT_OTHER_KEY)}
        </OtherOptionSection>
      )}
    </OptionsContainer>
  );

  const renderDesktopOptions = () => (
    <>
      {groups.map((group, groupIndex) => {
        const hasSubheading = !!group.subheading;
        const groupKey = getGroupKey(group, groupIndex);
        const showDivider = groupIndex > 0 && hasSubheading;

        return (
          <Fragment key={groupKey}>
            {showDivider && <GroupDivider />}
            {hasSubheading && (
              <GroupSubheading variant="body2Bold">
                {group.subheading}
              </GroupSubheading>
            )}
            {group.options.map(option => renderOption(option, groupKey))}
          </Fragment>
        );
      })}
      {otherOption && renderOption(otherOption, GROUPED_SELECT_OTHER_KEY)}
    </>
  );

  const renderBottomSheet = () => (
    <GroupedSelectBottomSheet
      open={isOpen}
      onClose={handleClose}
      hideBackdrop={false}
    >
      <BottomSheetHandle />
      <BottomSheetHeader>
        <Typography variant="body2Bold">{title}</Typography>
      </BottomSheetHeader>
      {renderOptionsContent()}
    </GroupedSelectBottomSheet>
  );

  const nativeSelectValue = getNativeSelectValue(perGroupSelection, value);

  const anchorWidth = selectAnchorRef.current?.clientWidth;
  const desktopMenuProps = isDesktop
    ? getDesktopMenuProps(anchorWidth)
    : undefined;

  return (
    <>
      <GroupedSelectControl>
        {label && (
          <GroupedSelectFieldLabel component="label" error={error}>
            {label}
          </GroupedSelectFieldLabel>
        )}
        <GroupedSelectFormControl fullWidth={fullWidth} error={error} sx={sx}>
          <Box ref={selectAnchorRef} width="100%">
            <MUISelect
              {...getDataTestId(`${name}-grouped-select`)}
              className={selectedLabel ? 'multiSelectItem' : undefined}
              fullWidth
              value={nativeSelectValue}
              open={isDesktop ? isOpen : false}
              disabled={disabled}
              onClick={!isDesktop && !disabled ? openOverlay : undefined}
              onOpen={isDesktop && !disabled ? openOverlay : undefined}
              onClose={isDesktop ? handleDesktopClose : undefined}
              displayEmpty
              IconComponent={() => <GroupedSelectSelectIcon isOpen={isOpen} />}
              renderValue={() => (
                <GroupedSelectDisplayValue
                  placeholder={placeholder}
                  selectedLabel={selectedLabel}
                />
              )}
              MenuProps={desktopMenuProps}
            >
              {isDesktop
                ? renderDesktopOptions()
                : allOptions.map(option => (
                    <option key={option.value} value={option.value} />
                  ))}
            </MUISelect>
          </Box>
          {helperText && (
            <GroupedSelectHelperText error={error} helperText={helperText} />
          )}
        </GroupedSelectFormControl>
      </GroupedSelectControl>
      {!isDesktop && renderBottomSheet()}
    </>
  );
}
